import React from 'react';
import { PipelineProject, PipelineStage, getStageIndex, getStage } from '../../types/pipeline';
import StatusBadge from './StatusBadge';

interface PipelineViewProps {
  projects: PipelineProject[];
  stages: PipelineStage[];
  onSelectProject: (project: PipelineProject) => void;
}

const STATUS_ROW_TINTS: Record<string, { bg: string; sticky: string }> = {
  active:    { bg: 'bg-emerald-50/50', sticky: 'bg-emerald-50/50' },
  at_risk:   { bg: 'bg-red-50/50',     sticky: 'bg-red-50/50' },
  cancelled: { bg: 'bg-gray-100/60',   sticky: 'bg-gray-100/60' },
  on_hold:   { bg: 'bg-amber-50/50',   sticky: 'bg-amber-50/50' },
  completed: { bg: 'bg-blue-50/50',    sticky: 'bg-blue-50/50' },
};

const PipelineView: React.FC<PipelineViewProps> = ({ projects, stages, onSelectProject }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[900px]">
        <thead>
          <tr>
            <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide w-48 sticky left-0 bg-white z-10">
              Program
            </th>
            {stages.map((stage) => (
              <th
                key={stage.id}
                className="px-1 py-2.5 text-xs font-medium text-gray-400 uppercase tracking-wide text-center"
                style={{ minWidth: 60 }}
                title={stage.description}
              >
                {stage.short}
              </th>
            ))}
            <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide text-center w-20">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project, rowIdx) => {
            const currentIdx = getStageIndex(project.stage);
            const currentStage = getStage(project.stage);
            const isCancelled = project.status === 'cancelled';

            const rowTint = STATUS_ROW_TINTS[project.status];

            return (
              <tr
                key={project.id}
                className={`group cursor-pointer hover:bg-gray-50 transition-colors border-t border-gray-100 ${rowTint?.bg || ''}`}
                onClick={() => onSelectProject(project)}
                style={{ animationDelay: `${rowIdx * 60}ms` }}
              >
                {/* Program name */}
                <td className={`px-3 py-3 sticky left-0 ${rowTint?.sticky || 'bg-white'} group-hover:bg-gray-50 z-10 transition-colors`}>
                  <div className="font-medium text-gray-900 text-sm">{project.name}</div>
                  <div className="text-xs text-gray-400 truncate">{project.target}</div>
                </td>

                {/* Stage columns */}
                {stages.map((stage, stageIdx) => {
                  const isCurrent = stage.id === project.stage;
                  const isPast = stageIdx < currentIdx;

                  return (
                    <td key={stage.id} className="px-1 py-3 text-center relative">
                      {/* Trail line for completed stages */}
                      {isPast && (
                        <div className="flex items-center justify-center">
                          <div
                            className="h-0.5 w-full rounded"
                            style={{ backgroundColor: `${currentStage?.color || '#94a3b8'}40` }}
                          />
                        </div>
                      )}

                      {/* Current stage dot */}
                      {isCurrent && (
                        <div className="flex items-center justify-center">
                          {isCancelled ? (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </div>
                          ) : (
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow-sm"
                              style={{ backgroundColor: stage.color }}
                            >
                              {project.compounds}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}

                {/* Status */}
                <td className="px-3 py-3 text-center">
                  <StatusBadge status={project.status} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PipelineView;
